import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { inventoryErrorKey } from '@/features/inventory/inventoryErrors';
import { Caution, Dialog, SECONDARY_BUTTON, toIsoInstant } from '@/features/inventory/registerUi';
import { useTenants } from '@/features/tenants/useTenants';
import { GoLivePanel } from './GoLivePanel';
import { useProvisionVehicle } from './useProvisioning';
import {
  draftToBody,
  EMPTY_DRAFT,
  Field,
  HardwareFields,
  IdentityFields,
  INPUT,
  PlanField,
  type Draft,
} from './vehicleFields';

/**
 * SELL A COMMISSIONED TRACKER TO A CUSTOMER (slice 18f).
 *
 * Mahad: *"when I sell to tenant and agree with plan, the installation should
 * happen because I already tested the device earlier and saw it under
 * commissioning or global view, then when I assign to vehicle it will be on that
 * vehicle."*
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THIS IS THE WIZARD'S STEPS 1–3 WITH THE DEVICE PICKER REPLACED BY A DEVICE.
 * ─────────────────────────────────────────────────────────────────────────────
 * The wizard starts from a TENANT and picks hardware. This starts from
 * HARDWARE — a unit already commissioned, already watched reporting — and picks
 * a tenant. Same `POST /admin/tenants/:tenantId/vehicles`, same fields, opposite
 * order, because that is the order the sale actually happens in.
 *
 * 🔴 THE FIELDS ARE IMPORTED, NOT COPIED. `vehicleFields.tsx` owns every label,
 * option list and body mapping. Two forms posting the same body from two
 * hand-written field sets is how one of them ends up sending a command password
 * for `osmand`, or an untrimmed plate, six months from now.
 *
 * 🔴 AND THE SUCCESS STATE IS `GoLivePanel`, NOT A "SAVED" TOAST. The vehicle
 * exists at that moment and the customer CANNOT SEE IT. Ending on "Saved"
 * teaches an operator the job is done; it is not, and the same panel the wizard
 * and `/vehicles` use says so in the same words.
 */

/** The commissioned tracker being sold. Not `AdminDevice`: `/commissioning` and
 * `/devices` name its uuid differently and neither shape is this dialog's. */
export interface AssignTarget {
  deviceId: string;
  imei: string;
  model: string | null;
  simIccid: string | null;
  simMsisdn: string | null;
  simCarrier: string | null;
}

export function AssignDeviceDialog({
  device,
  onClose,
}: {
  device: AssignTarget;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const tenantsQ = useTenants();
  const [tenantId, setTenantId] = useState('');
  const [draft, setDraft] = useState<Draft>({ ...EMPTY_DRAFT, deviceId: device.deviceId });
  const [installedAt, setInstalledAt] = useState('');
  const [errorKey, setErrorKey] = useState<string | null>(null);
  const [vehicleId, setVehicleId] = useState<string | null>(null);

  const provision = useProvisionVehicle(tenantId);
  const set = <K extends keyof Draft>(k: K, v: Draft[K]) => setDraft((d) => ({ ...d, [k]: v }));

  const tenants = Array.isArray(tenantsQ.data?.tenants) ? tenantsQ.data.tenants : [];
  const canSave = tenantId !== '' && draft.plate.trim().length > 0 && !provision.isPending;

  async function submit() {
    setErrorKey(null);
    try {
      const at = toIsoInstant(installedAt);
      const res = await provision.mutateAsync({
        ...draftToBody(draft),
        // Omitted entirely when blank — the server fills in "now". Sending
        // `undefined` explicitly would be the same thing, but an absent key is
        // what the `.strict()` schema is happiest with.
        ...(at ? { at } : {}),
      });
      setVehicleId(res.vehicleId);
    } catch (e) {
      setErrorKey(inventoryErrorKey(e));
    }
  }

  // ── After the sale: the go-live gate, not a "saved" message. ───────────────
  if (vehicleId !== null) {
    return (
      <Dialog title={t('assign.doneTitle', { plate: draft.plate.trim() })} onClose={onClose} wide>
        {/* 🔴 NO not-live banner here. GoLivePanel owns the whole go-live
            narrative and it is the only thing that knows the state. This dialog
            used to render `provision.notLiveYet` unconditionally, so after a
            successful go-live the operator saw BOTH "the customer cannot see it
            yet" AND "Live. The customer can now see this vehicle." at the same
            time. Two components with independent opinions about one fact. */}
        <GoLivePanel vehicleId={vehicleId} liveAt={null} autoRun />
        <div className="mt-5 flex justify-end">
          <button type="button" className={SECONDARY_BUTTON} onClick={onClose}>
            {t('inventory.close')}
          </button>
        </div>
      </Dialog>
    );
  }

  return (
    <Dialog title={t('assign.title', { imei: device.imei })} onClose={onClose} wide>
      <div className="grid gap-4">
        {/* The hardware, read-only. There is no IMEI input and no SIM input
            anywhere in this dialog — the registers own both (slice 18d). */}
        <div className="grid gap-1 rounded-lg border border-raad-border p-3">
          <p className="text-[12.5px]">
            <span className="text-raad-muted">{t('inventory.devices.imei')}: </span>
            <span className="num">{device.imei}</span>
            {device.model ? <span className="text-raad-muted"> · {device.model}</span> : null}
          </p>
          <p className="text-[12.5px]">
            <span className="text-raad-muted">{t('inventory.devices.sim')}: </span>
            {device.simIccid === null ? (
              <span className="text-raad-warn">{t('provision.device.noSim')}</span>
            ) : (
              <>
                <span className="num">{device.simIccid}</span>
                {device.simMsisdn === null ? (
                  <span className="text-raad-warn"> · {t('provision.device.simNotActivated')}</span>
                ) : (
                  <span className="num"> · {device.simMsisdn}</span>
                )}
                {device.simCarrier ? (
                  <span className="text-raad-muted"> · {device.simCarrier}</span>
                ) : null}
              </>
            )}
          </p>
          {device.simIccid !== null && device.simMsisdn === null ? (
            <Caution>{t('provision.device.simNotActivatedWarning')}</Caution>
          ) : null}
        </div>

        <Field label={t('assign.tenant')} hint={t('assign.tenantHint')}>
          <select
            className={INPUT}
            value={tenantId}
            onChange={(e) => setTenantId(e.target.value)}
            autoFocus
          >
            <option value="">{t('assign.tenantPlaceholder')}</option>
            {tenants.map((x) => (
              <option key={x.id} value={x.id}>
                {x.name}
              </option>
            ))}
          </select>
        </Field>
        {tenantsQ.isPending ? (
          <p className="text-[11.5px] text-raad-muted">{t('inventory.loading')}</p>
        ) : tenants.length === 0 ? (
          <p className="text-[11.5px] text-raad-muted">{t('assign.noTenants')}</p>
        ) : null}

        <div className="grid gap-4 sm:grid-cols-2">
          <IdentityFields draft={draft} set={set} />
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <HardwareFields draft={draft} set={set} />
          <PlanField draft={draft} set={set} />
          {/* 🔴 WHEN IT WAS PHYSICALLY FITTED, not when this form was submitted.
              It becomes the assignment's `from_at` AND the trip-coverage
              watermark, so a technician recording this evening what they did
              this morning must not lose the morning. Blank means now. */}
          <Field label={t('assign.installedAt')} hint={t('assign.installedAtHint')}>
            <input
              className={INPUT}
              type="datetime-local"
              value={installedAt}
              onChange={(e) => setInstalledAt(e.target.value)}
            />
          </Field>
        </div>

        {errorKey !== null ? (
          <p
            role="alert"
            className="rounded-lg border border-raad-error/40 bg-raad-error/10 p-3 text-[12.5px] text-raad-text"
          >
            {t(errorKey)}
          </p>
        ) : null}
      </div>

      <div className="mt-5 flex items-center justify-end gap-3">
        <button type="button" className={SECONDARY_BUTTON} onClick={onClose}>
          {t('inventory.close')}
        </button>
        <button
          type="button"
          onClick={() => void submit()}
          disabled={!canSave}
          className="min-h-10 rounded-lg bg-raad-primary px-4 text-[13px] font-semibold text-raad-onprimary disabled:opacity-40"
        >
          {provision.isPending ? t('provision.saving') : t('assign.assign')}
        </button>
      </div>
    </Dialog>
  );
}
